import { useCreateSearchImageUploadUrlMutation } from '@/shared/hooks/queries/generated';
import i18n from '@/shared/i18n';

// ============================================================================
// Upload d'une image de requête (recherche par image, changelog 2026-08-02) :
// 1. createSearchImageUploadUrl -> URL présignée (valable 300 s : on uploade
//    immédiatement après sélection du fichier, pas au submit)
// 2. POST multipart : les `fields` de politique S3/Wasabi D'ABORD, le fichier
//    EN DERNIER (ordre imposé par le stockage), aucun autre champ.
// `key` va dans SearchCriteriaInput.imageKey ; `publicUrl` sert à l'aperçu.
// ============================================================================

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024; // 20 Mio (borne stockage)

export interface UploadedSearchImage {
  key: string;
  publicUrl: string;
}

// L'API ne stocke que la clé (imageKey) dans le filtre — la publicUrl n'est
// rendue qu'au moment de l'upload. On garde la correspondance clé -> URL en
// local pour pouvoir RÉAFFICHER l'image quand on inspecte la recherche
// (limité au navigateur où elle a été créée ; repli sur un simple indicateur).
const IMAGE_URLS_KEY = 'recop.searchImageUrls.v1';
const IMAGE_URLS_MAX = 100;

const loadImageUrlMap = (): Record<string, string> => {
  try {
    return (JSON.parse(window.localStorage.getItem(IMAGE_URLS_KEY) ?? '{}') as Record<string, string>) ?? {};
  } catch {
    return {};
  }
};

const rememberSearchImageUrl = (key: string, publicUrl: string): void => {
  try {
    const map = loadImageUrlMap();
    map[key] = publicUrl;
    // Borne la taille : on écarte les entrées les plus anciennes (ordre d'insertion)
    const entries = Object.entries(map);
    const bounded = entries.slice(Math.max(0, entries.length - IMAGE_URLS_MAX));
    window.localStorage.setItem(IMAGE_URLS_KEY, JSON.stringify(Object.fromEntries(bounded)));
  } catch {
    // stockage indisponible : l'aperçu retombera sur l'indicateur
  }
};

/** URL publique d'une image de recherche déjà téléversée (si connue ici). */
export const getSearchImagePublicUrl = (key: string): string | null => loadImageUrlMap()[key] ?? null;

export const uploadSearchImage = async (file: File): Promise<UploadedSearchImage> => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(i18n.t('searchModal.imageFormatUnsupported'));
  }
  if (file.size < 1 || file.size > MAX_IMAGE_BYTES) {
    throw new Error(i18n.t('searchModal.imageTooLarge'));
  }

  const res = await useCreateSearchImageUploadUrlMutation.fetcher({
    input: { contentType: file.type, contentLength: file.size },
  })();
  const { uploadUrl, fields, key, publicUrl } = res.createSearchImageUploadUrl;

  const formData = new FormData();
  Object.entries(fields as Record<string, string>).forEach(([name, value]) => {
    formData.append(name, value);
  });
  formData.append('file', file);

  const upload = await fetch(uploadUrl, { method: 'POST', body: formData });
  if (!upload.ok) {
    throw new Error(i18n.t('searchModal.imageUploadFailedRetry'));
  }

  rememberSearchImageUrl(key, publicUrl);
  return { key, publicUrl };
};
